/**
 * 時間制の席料の設定。
 *
 * コンカフェ（15分・30分単位、時間帯で単価が変わる）と、
 * キャバクラ（初回は入店時刻で決まり、延長は一律）の両方をここで作る。
 *
 * 計算そのものは utils/timeCharge.ts の1箇所。
 * この画面は入れる・見せるだけで、金額の決め方をここに書かない。
 * 2箇所に計算があると、片方だけ直したときに
 * レジとアプリで会計が食い違う。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Paper,
  Select, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ALLOWED_UNIT_MINUTES, ALLOWED_ROUNDING_UNITS, DEFAULT_TIME_CHARGE,
  computeTimeCharge, toMinutes, validateTimeCharge,
  type PricingMode, type TimeChargeBand, type TimeChargeSettings as Settings,
} from '../../utils/timeCharge';
import { resolveTaxRate, taxOn } from '../../utils/taxRate';

const MODE_LABEL: Record<PricingMode, string> = {
  perUnitBand: '時間帯ごと（30分ごとに引き直す）',
  entryBand: '入店した時刻で決める（最後まで同じ）',
  flat: '時間帯によらず一律',
};

/** 業態ごとのよくある形。入れる手間を減らすためのひな形。 */
const PRESETS: Record<string, { label: string; note: string; value: Settings }> = {
  concafe: {
    label: 'コンカフェ（30分・時間帯で変わる）',
    note: '13:00〜18:00 は30分1,200円、18:00〜25:00 は30分1,400円。1時間以内は別途500円。',
    value: {
      ...DEFAULT_TIME_CHARGE, unitMinutes: 30,
      bands: [
        { start: '13:00', end: '18:00', unitPrice: 1200, extensionUnitPrice: 1200 },
        { start: '18:00', end: '25:00', unitPrice: 1400, extensionUnitPrice: 1400 },
      ],
      serviceChargeRate: 0.05, roundingUnit: 10, setUnits: 1,
      shortStayMinutes: 60, shortStayFee: 500,
    },
  },
  concafe15: {
    label: 'コンカフェ（15分単位）',
    note: '15分ごとに課金する店。短い滞在の客が多い立地で使われる。',
    value: {
      ...DEFAULT_TIME_CHARGE, unitMinutes: 15,
      bands: [{ start: '12:00', end: '24:00', unitPrice: 700, extensionUnitPrice: 700 }],
      serviceChargeRate: 0.1, roundingUnit: 10, setUnits: 1,
    },
  },
  cabaret: {
    label: 'キャバクラ（初回は入店時刻・延長は一律）',
    note: '早い時間ほど初回が安く、延長はいつ入っても同じ額。',
    value: {
      ...DEFAULT_TIME_CHARGE, unitMinutes: 60,
      bands: [
        { start: '19:00', end: '21:00', unitPrice: 4000 },
        { start: '21:00', end: '24:00', unitPrice: 6000 },
        { start: '24:00', end: '29:00', unitPrice: 8000 },
      ],
      setPricing: 'entryBand', extensionPricing: 'flat', flatExtensionPrice: 5000,
      serviceChargeRate: 0.2, roundingUnit: 100, setUnits: 1,
    },
  },
};

const TimeChargeSettingsPage: React.FC = () => {
  const { shop } = useAuth() as any;
  const [s, setS] = useState<Settings>(DEFAULT_TIME_CHARGE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!shop?.id) return;
    (async () => {
      try {
        const d = await getDoc(doc(db, 'shops', shop.id));
        const raw = d.exists() ? (d.data() as any).timeCharge : null;
        if (raw) setS({ ...DEFAULT_TIME_CHARGE, ...raw });
      } catch (e: any) {
        setError(e?.message ?? '設定を読めませんでした');
      } finally { setLoading(false); }
    })();
  }, [shop?.id]);

  const errs = useMemo(() => validateTimeCharge(s), [s]);

  /** 入れた値でいくらになるかを、その場で出す。 */
  const preview = useMemo(() => {
    if (errs.length > 0) return null;
    const cases = [
      { label: '45分いた', start: s.bands[0]?.start ?? '13:00', stay: 45 },
      { label: '2時間いた', start: s.bands[0]?.start ?? '13:00', stay: 120 },
      { label: '時間帯をまたいだ1時間', start: s.bands[1]?.start
          ? `${String(Math.floor((toMinutes(s.bands[1].start) - 30) / 60)).padStart(2, '0')}:${String((toMinutes(s.bands[1].start) - 30) % 60).padStart(2, '0')}`
          : (s.bands[0]?.start ?? '13:00'), stay: 60 },
    ];
    return cases.map((c) => {
      try {
        const r = computeTimeCharge(s, toMinutes(c.start), c.stay);
        const tax = taxOn(r.beforeTax, shop);
        return { ...c, r, tax, total: r.beforeTax + tax, err: null as string | null };
      } catch (e: any) {
        return { ...c, r: null, tax: 0, total: 0, err: String(e?.message ?? e) };
      }
    });
  }, [s, errs, shop]);

  const setBand = (i: number, patch: Partial<TimeChargeBand>) =>
    setS((p) => ({ ...p, bands: p.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  const save = async () => {
    if (errs.length > 0) return;   // 壊れた設定は保存させない
    setSaving(true); setError(null); setSaved(false);
    try {
      await updateDoc(doc(db, 'shops', shop.id), { timeCharge: s, updatedAt: new Date() });
      setSaved(true);
    } catch (e: any) {
      setError(e?.message ?? '保存できませんでした');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>時間制の席料</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        15分・30分といった単位で席料を取る店の設定です。
        時間帯で単価が変わる店、入店した時刻で初回が決まる店のどちらにも対応します。
        消費税はここでは設定しません（店舗設定の税率をそのまま使います）。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }}>保存しました</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>よくある形から入れる</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(PRESETS).map(([k, p]) => (
            <Button key={k} size="small" variant="outlined" onClick={() => setS(p.value)}>
              {p.label}
            </Button>
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>基本</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>時間の単位</InputLabel>
            <Select label="時間の単位" value={s.unitMinutes}
                    onChange={(e) => setS({ ...s, unitMinutes: Number(e.target.value) as any })}>
              {ALLOWED_UNIT_MINUTES.map((u) => <MenuItem key={u} value={u}>{u} 分</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="セットの単位数" type="number"
            helperText="ここまでがセット。以降は延長の単価になります"
            value={s.setUnits}
            onChange={(e) => setS({ ...s, setUnits: Math.max(0, Number(e.target.value) || 0) })} />
          <TextField size="small" label="サービス料" type="number"
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            value={Math.round(s.serviceChargeRate * 1000) / 10}
            onChange={(e) => setS({ ...s, serviceChargeRate: (Number(e.target.value) || 0) / 100 })} />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>端数の切り上げ</InputLabel>
            <Select label="端数の切り上げ" value={s.roundingUnit}
                    onChange={(e) => setS({ ...s, roundingUnit: Number(e.target.value) })}>
              {ALLOWED_ROUNDING_UNITS.map((u) => (
                <MenuItem key={u} value={u}>{u === 1 ? '切り上げない' : `${u} 円単位`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>単価の決め方</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>セット</InputLabel>
            <Select label="セット" value={s.setPricing}
                    onChange={(e) => setS({ ...s, setPricing: e.target.value as PricingMode })}>
              {(Object.keys(MODE_LABEL) as PricingMode[]).map((m) => (
                <MenuItem key={m} value={m}>{MODE_LABEL[m]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>延長</InputLabel>
            <Select label="延長" value={s.extensionPricing}
                    onChange={(e) => setS({ ...s, extensionPricing: e.target.value as PricingMode })}>
              {(Object.keys(MODE_LABEL) as PricingMode[]).map((m) => (
                <MenuItem key={m} value={m}>{MODE_LABEL[m]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        {(s.setPricing === 'flat' || s.extensionPricing === 'flat') && (
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            {s.setPricing === 'flat' && (
              <TextField size="small" label="セットの単価（一律）" type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
                value={s.flatSetPrice}
                onChange={(e) => setS({ ...s, flatSetPrice: Math.max(0, Number(e.target.value) || 0) })} />
            )}
            {s.extensionPricing === 'flat' && (
              <TextField size="small" label="延長の単価（一律）" type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
                value={s.flatExtensionPrice}
                onChange={(e) => setS({ ...s, flatExtensionPrice: Math.max(0, Number(e.target.value) || 0) })} />
            )}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">時間帯ごとの単価</Typography>
          <Button size="small" startIcon={<AddIcon />}
            onClick={() => setS({ ...s, bands: [...s.bands, { start: '18:00', end: '25:00', unitPrice: 0 }] })}>
            時間帯を足す
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          閉店が翌日にかかる店は 25:00 のように 24 を超えた書き方をしてください。
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {s.bands.map((b, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="開始" sx={{ width: 110 }} value={b.start}
                onChange={(e) => setBand(i, { start: e.target.value })} />
              <TextField size="small" label="終了" sx={{ width: 110 }} value={b.end}
                onChange={(e) => setBand(i, { end: e.target.value })} />
              <TextField size="small" label={`${s.unitMinutes}分あたり`} type="number" sx={{ width: 150 }}
                InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
                value={b.unitPrice}
                onChange={(e) => setBand(i, { unitPrice: Math.max(0, Number(e.target.value) || 0) })} />
              <TextField size="small" label="延長（空なら同額）" type="number" sx={{ width: 170 }}
                InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
                value={b.extensionUnitPrice ?? ''}
                onChange={(e) => setBand(i, {
                  extensionUnitPrice: e.target.value === '' ? undefined : Math.max(0, Number(e.target.value) || 0),
                })} />
              <IconButton size="small" onClick={() => setS({ ...s, bands: s.bands.filter((_, j) => j !== i) })}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>短い滞在とミニマムチャージ</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField size="small" label="この時間より短いと追加" type="number"
            InputProps={{ endAdornment: <InputAdornment position="end">分</InputAdornment> }}
            helperText="0 なら追加しません"
            value={s.shortStayMinutes}
            onChange={(e) => setS({ ...s, shortStayMinutes: Math.max(0, Number(e.target.value) || 0) })} />
          <TextField size="small" label="追加する額" type="number"
            InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
            value={s.shortStayFee}
            onChange={(e) => setS({ ...s, shortStayFee: Math.max(0, Number(e.target.value) || 0) })} />
          <TextField size="small" label="ミニマムチャージ" type="number"
            InputProps={{ endAdornment: <InputAdornment position="end">円</InputAdornment> }}
            helperText="席料がこの額に満たなければ底上げします。0 なら無し"
            value={s.minimumCharge}
            onChange={(e) => setS({ ...s, minimumCharge: Math.max(0, Number(e.target.value) || 0) })} />
        </Stack>
      </Paper>

      {errs.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errs.map((e, i) => <div key={i}>{e}</div>)}
        </Alert>
      )}

      {preview && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>この設定だといくらになるか</Typography>
            <Typography variant="caption" color="text.secondary">
              消費税 {Math.round(resolveTaxRate(shop) * 100)}% を含めた、お客様に出す金額です。
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              {preview.map((p, i) => (
                <Box key={i}>
                  {p.err ? (
                    <Typography variant="body2" color="error">{p.label}：{p.err}</Typography>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Chip size="small" label={`${p.start} から ${p.label}`} />
                        <Typography variant="h6">{p.total.toLocaleString()} 円</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        席料 {p.r!.seatCharge.toLocaleString()} 円（{p.r!.units} 単位）
                        {p.r!.shortStayFee > 0 && ` ＋ 短い滞在 ${p.r!.shortStayFee.toLocaleString()} 円`}
                        {p.r!.minimumTopUp > 0 && ` ＋ ミニマム底上げ ${p.r!.minimumTopUp.toLocaleString()} 円`}
                        {p.r!.serviceCharge > 0 && ` ＋ サービス料 ${p.r!.serviceCharge.toLocaleString()} 円`}
                        {p.r!.rounding > 0 && ` ＋ 端数切上 ${p.r!.rounding} 円`}
                        {` ＋ 消費税 ${p.tax.toLocaleString()} 円`}
                      </Typography>
                    </>
                  )}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Button variant="contained" onClick={save} disabled={saving || errs.length > 0}>
        {saving ? '保存しています…' : '保存する'}
      </Button>
    </Box>
  );
};

export default TimeChargeSettingsPage;
